import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, TextField, Typography } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { shortLabel } from "../utils/policyParts";
import {
  STAGE_ORDER,
  canRunNow,
  insurerOf,
  manualChanges,
  manualStepProblem,
  stageOf,
  stageParts,
  stageStatusMeta,
  stageTitle,
} from "../utils/relianceStages";
import { SetStagesByHand } from "../../../../Service/_index";
import { existingTpModeOfPolicy, routingOf, tpDetailsOpen } from "../utils/companyRules";
import { TpDetailsFields, tpDetailsOf, tpDetailsProblem } from "./TpPolicyDialog";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const LOOK = {
  completed: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  failed: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  pending: { color: "#667085", bg: "#F9FAFB", border: "#EAECF0" },
};
const btn = { textTransform: "none", borderRadius: "8px", fontFamily: FONT, fontWeight: 600, fontSize: "13px", boxShadow: "none" };

// index.scss makes every outlined input border transparent: restated here so each box is visibly a box
const boxedField = {
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "8px" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD !important" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#98A2B3 !important" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E3A5F !important", borderWidth: "2px !important" },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#B42318 !important" },
};

const isKshema = (part) => /kshema/i.test(String((part && part.company) || ""));

/** The Pending / Completed switch of one step. */
const Choice = ({ value, onChange, stage, disabled }) => (
  <Box sx={{ display: "inline-flex", border: "1px solid #D0D5DD", borderRadius: "8px", overflow: "hidden", flexShrink: 0, opacity: disabled ? 0.55 : 1 }}>
    {["pending", "completed"].map((v) => {
      const on = value === v;
      const look = v === "completed" ? LOOK.completed : { color: NAVY, bg: "#EEF3F9" };
      return (
        <Box
          key={v}
          component="button"
          type="button"
          disabled={disabled}
          data-testid={`status-choice-${stage}-${v}`}
          aria-pressed={on}
          onClick={() => onChange(v)}
          sx={{
            border: 0,
            borderLeft: v === "completed" ? "1px solid #D0D5DD" : 0,
            px: 1.25,
            height: 30,
            font: "inherit",
            fontFamily: FONT,
            fontSize: "12px",
            fontWeight: 600,
            cursor: disabled ? "default" : "pointer",
            color: on ? look.color : "#667085",
            backgroundColor: on ? look.bg : "#fff",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {on && v === "completed" ? <CheckRoundedIcon sx={{ fontSize: 14 }} /> : null}
          {v === "completed" ? "Completed" : "Pending"}
        </Box>
      );
    })}
  </Box>
);

/**
 * Edit -> Status: set the steps of one part by hand (2026-10-19). A Proposal is Completed only with its referral number
 * (KSHEMA its page link); KYC any time once the Proposal is done (KYC uploaded by hand at the portal); the payment link
 * any time, never a wallet payment. A reason is required. Then "Only update" (runs when the automation is free) or
 * "Update & run" — or just "Save" when every step is completed (nothing left to run). The server checks it all again.
 */
const StageStatusDialog = ({ policy, onClose, onChanged }) => {
  const open = !!policy;
  const parts = useMemo(() => stageParts(policy && policy.relianceStageView), [policy]);
  // Two insurers: Reliance OD waits for the TP policy, so its steps are changed only once the TP part is finished (the
  // owner, 2026-10-23: "without tp finished not allow to edit the od company"). The server refuses it too.
  const odLocked = (p) => !!p && p.key === "od" && existingTpModeOfPolicy(policy) === "optional" && !tpDetailsOpen(policy);
  const [partKey, setPartKey] = useState(null);
  const part = parts.find((p) => p.key === partKey) || parts.find((p) => !odLocked(p)) || parts[0] || null;
  const [picked, setPicked] = useState({});
  const [number, setNumber] = useState("");
  const [link, setLink] = useState("");
  const [reason, setReason] = useState("");
  const [tpDetails, setTpDetails] = useState({ policyNumber: "", startDate: "", endDate: "" });
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");

  // a fresh form for every policy / part
  useEffect(() => {
    if (!part) return;
    const row = (part.stages || []).find((s) => s.stage === "proposal") || {};
    // starts as it is now (a Failed step shows neither choice until one is picked)
    setPicked(Object.fromEntries(STAGE_ORDER.map((s) => [s, stageOf(part, s)])));
    setNumber(row.proposalNumber || (policy && policy.proposalNumber) || row.previousProposalNumber || "");
    setLink(row.link || (policy && policy.proposalLink) || "");
    setReason("");
    setTpDetails(tpDetailsOf(policy));
    setServerError("");
  }, [part && part.key, policy]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !part) return null;
  const locked = odLocked(part);
  const tpPart = parts.find((p) => p.key === "tp");
  const tpInsurer = routingOf(policy).TPCompany || (tpPart ? insurerOf(tpPart) : "The");

  const paymentMethod = policy.Paymentmethod || policy.paymentMode || "";
  const changes = manualChanges(part, picked, { number, link });
  const proposalDone = picked.proposal === "completed";
  const problems = Object.fromEntries(
    changes.map((c) => [
      c.stage,
      manualStepProblem({ part, stage: c.stage, to: c.status, number, link, paymentMethod, proposalDone: c.stage === "kyc" ? proposalDone : undefined }),
    ])
  );
  const allDoneAfter = STAGE_ORDER.every((s) => picked[s] === "completed");
  // The TP part of a two-insurer policy finished here (Proposal, KYC and Payment all Completed): the TP policy is issued
  // only then, so its number and dates are asked (Reliance OD needs them and never starts without them). The owner's
  // rule, 2026-10-22. The server checks them again.
  const asksTp =
    part.key === "tp" &&
    existingTpModeOfPolicy(policy) === "optional" &&
    allDoneAfter &&
    changes.some((c) => c.status === "completed");
  const tpProblem = asksTp ? tpDetailsProblem(tpDetails) : null;
  const firstProblem = Object.values(problems).find(Boolean) || tpProblem || null;
  const reasonOk = reason.trim().length >= 3;
  const canSave = !locked && changes.length > 0 && !firstProblem && reasonOk && !busy;
  const runnable = canRunNow(policy.jobStatus || policy.status);
  const name = policy.fullName || policy.firstName || policy.policyId || "This policy";

  const pick = (stage, value) => {
    setServerError("");
    setPicked((p) => {
      const next = { ...p, [stage]: value };
      // a Proposal back to Pending takes KYC and Payment with it (a new proposal is made)
      if (stage === "proposal" && value === "pending" && p.proposal !== "pending") {
        if (next.kyc === "completed") next.kyc = "pending";
        if (next.payment === "completed") next.payment = "pending";
      }
      return next;
    });
  };

  const save = async (run) => {
    if (!canSave) return;
    setBusy(true);
    setServerError("");
    try {
      const res = await SetStagesByHand({
        policyId: policy._id || policy.id,
        part: part.key,
        changes,
        reason: reason.trim(),
        run: !!run,
        ...(asksTp ? { tpPolicy: tpDetails } : {}),
      });
      if (res && !res.error) {
        if (onChanged) onChanged(res.data);
        onClose();
      } else {
        setServerError((res && res.message) || "Could not save the steps");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: undefined } } }}>
      <Box sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="stage-status-dialog">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: "11px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY, backgroundColor: "#EEF3F9", border: "1px solid #C9D7E8" }}>
            <TuneRoundedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: NAVY, fontFamily: FONT, lineHeight: 1.3 }}>Change the status by hand</Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#475467", fontFamily: FONT }}>
              {name} · {insurerOf(part)} {part.label || shortLabel(part.key)}
            </Typography>
          </Box>
        </Box>

        {parts.length > 1 ? (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            {parts.map((p) => (
              <Button
                key={p.key}
                size="small"
                variant={p.key === part.key ? "contained" : "outlined"}
                onClick={() => setPartKey(p.key)}
                disabled={busy}
                sx={{ ...btn, fontSize: "12px", ...(p.key === part.key ? { backgroundColor: NAVY } : { color: NAVY, borderColor: "#C9D7E8" }) }}
              >
                {insurerOf(p)} {p.label || shortLabel(p.key)}
              </Button>
            ))}
          </Box>
        ) : null}

        <Box sx={{ border: "1px solid #EAECF0", borderRadius: "12px", overflow: "hidden", mb: 1.5 }}>
          {STAGE_ORDER.map((stage, i) => {
            const row = (part.stages || []).find((s) => s.stage === stage) || { stage };
            const meta = stageStatusMeta(row.status);
            const look = LOOK[meta.status] || LOOK.pending;
            const problem = problems[stage];
            const changed = changes.some((c) => c.stage === stage);
            return (
              <Box key={stage} data-testid={`status-row-${stage}`} sx={{ px: 1.75, py: 1.25, borderTop: i ? "1px solid #F2F4F7" : 0, backgroundColor: changed ? "#FCFCFD" : "#fff" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, fontFamily: FONT, color: look.color, backgroundColor: look.bg, border: `1.5px solid ${look.color}` }}>
                    {i + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 120 }}>
                    <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: NAVY, fontFamily: FONT, lineHeight: "18px" }}>
                      {stageTitle(row, { paymentMethod, company: part.company }).replace(/^\d+ /, "")}
                    </Typography>
                    <Typography sx={{ fontSize: "11.5px", color: look.color, fontFamily: FONT, lineHeight: "16px" }}>
                      Now {meta.label}
                      {row.manual ? " (set by hand)" : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: { xs: "36px", sm: 0 } }}>
                    <Choice value={picked[stage]} onChange={(v) => pick(stage, v)} stage={stage} disabled={busy || locked} />
                  </Box>
                </Box>

                {stage === "proposal" && picked.proposal === "completed" ? (
                  <TextField
                    size="small"
                    fullWidth
                    disabled={busy}
                    label={isKshema(part) ? "KSHEMA proposal page link" : "Referral number"}
                    placeholder={isKshema(part) ? "https://…" : "R141026119597"}
                    value={isKshema(part) ? link : number}
                    onChange={(e) => (isKshema(part) ? setLink(e.target.value) : setNumber(e.target.value.toUpperCase()))}
                    inputProps={{ "data-testid": "status-proposal-id" }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ...boxedField, mt: 1.25, "& .MuiInputBase-input": { fontFamily: FONT, fontSize: "13px" } }}
                  />
                ) : null}

                {problem ? (
                  <Typography data-testid={`status-problem-${stage}`} sx={{ mt: 0.75, fontSize: "12px", color: LOOK.failed.color, fontFamily: FONT, lineHeight: "17px" }}>
                    {problem}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>

        {/* Every step of the TP part completed: the issued TP policy's number and dates */}
        {asksTp ? (
          <Box data-testid="status-tp-policy" sx={{ border: "1px solid #EAECF0", borderRadius: "12px", px: 1.75, py: 1.25, mb: 1.5, backgroundColor: "#FCFCFD" }}>
            <Typography sx={{ fontSize: "13.5px", fontWeight: 600, color: NAVY, fontFamily: FONT, lineHeight: "18px" }}>Issued TP policy</Typography>
            <TpDetailsFields
              value={tpDetails}
              onChange={(v) => {
                setServerError("");
                setTpDetails(v);
              }}
              disabled={busy}
              insurer={routingOf(policy).TPCompany || insurerOf(part)}
              years={policy.paCoverYears}
            />
            {tpProblem ? (
              <Typography data-testid="status-problem-tp" sx={{ mt: 0.75, fontSize: "12px", color: LOOK.failed.color, fontFamily: FONT, lineHeight: "17px" }}>
                {tpProblem}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <TextField
          fullWidth
          multiline
          rows={2}
          required
          disabled={busy || locked}
          label="Reason"
          placeholder="For example KYC uploaded by hand at Reliance"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ "data-testid": "status-reason" }}
          InputLabelProps={{ shrink: true }}
          helperText={reasonOk ? "Saved in the history with your name and the time" : "Required - why the status is changed by hand"}
          sx={{ ...boxedField, mt: 0.5, "& .MuiInputBase-input": { fontFamily: FONT, fontSize: "13px" }, "& .MuiFormHelperText-root": { fontFamily: FONT, ml: 0 } }}
        />

        {serverError ? (
          <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: LOOK.failed.bg, border: `1px solid ${LOOK.failed.border}` }}>
            <Typography data-testid="status-server-error" sx={{ fontSize: "12.5px", color: LOOK.failed.color, fontFamily: FONT }}>
              {serverError}
            </Typography>
          </Box>
        ) : null}

        {locked ? (
          <Box data-testid="status-od-locked" sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: "#FFFAEB", border: "1px solid #FEDF89" }}>
            <Typography sx={{ fontSize: "12.5px", color: "#B54708", fontFamily: FONT }}>
              {tpInsurer} TP is not finished yet, so Reliance OD cannot be changed. Finish the TP part first.
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ mt: 1.5, fontSize: "12px", color: "#475467", fontFamily: FONT }}>
            {asksTp
              ? "Every step will be completed, so the TP part is marked completed. Reliance OD then starts with these TP policy details."
              : allDoneAfter
              ? "Every step will be completed, so the policy part is marked completed and nothing runs."
              : "Only update = it runs when the automation is free. Update & run = it is queued now and continues from the first step that is not completed."}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2, flexWrap: "wrap" }}>
          <Button onClick={onClose} disabled={busy} sx={{ ...btn, color: "#475467" }}>
            Cancel
          </Button>
          {allDoneAfter ? (
            <Button variant="contained" disabled={!canSave} onClick={() => save(false)} data-testid="status-save" sx={{ ...btn, backgroundColor: NAVY }}>
              {busy ? "Saving…" : "Save"}
            </Button>
          ) : (
            <>
              <Button variant="outlined" disabled={!canSave} onClick={() => save(false)} data-testid="status-only-update" sx={{ ...btn, color: NAVY, borderColor: "#C9D7E8" }}>
                Only update
              </Button>
              {runnable ? (
                <Button variant="contained" disabled={!canSave} onClick={() => save(true)} data-testid="status-update-run" sx={{ ...btn, backgroundColor: NAVY }}>
                  {busy ? "Saving…" : "Update & run"}
                </Button>
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default StageStatusDialog;
